/**
 * Ticket Priority Badge Component
 *
 * Displays ticket priority level with appropriate color coding.
 * Uses existing Badge component with semantic colors.
 */

import React from 'react';
import { Badge } from '../common/Badge';
import { TicketPriority } from '../../types/support';
import type { BadgeProps } from '../common/Badge';

export interface TicketPriorityBadgeProps {
  /** Ticket priority to display */
  priority?: TicketPriority;

  /** Badge size */
  size?: BadgeProps['size'];

  /** Show priority dot indicator */
  dot?: boolean;
}

/**
 * Maps ticket priority to badge variant and label
 */
const getPriorityConfig = (
  priority: TicketPriority
): { variant: BadgeProps['variant']; label: string } => {
  switch (priority) {
    case TicketPriority.LOW:
      return { variant: 'default', label: 'Low' };
    case TicketPriority.MEDIUM:
      return { variant: 'info', label: 'Medium' };
    case TicketPriority.HIGH:
      return { variant: 'warning', label: 'High' };
    case TicketPriority.URGENT:
      return { variant: 'error', label: 'Urgent' };
    default:
      return { variant: 'default', label: 'Unknown' };
  }
};

export const TicketPriorityBadge: React.FC<TicketPriorityBadgeProps> = ({
  priority,
  size = 'md',
  dot = false,
}) => {
  if (!priority) {
    return null;
  }

  const { variant, label } = getPriorityConfig(priority);

  return (
    <Badge variant={variant} size={size} dot={dot}>
      {label}
    </Badge>
  );
};
